import * as PIXI from 'pixi.js';

/**
 * Captures a thumbnail/snapshot of the canvas at a specific time
 * @param {Object} options - Configuration options
 * @param {Object} options.viewport - The PIXI viewport
 * @param {Object} options.pixiApp - The PIXI application
 * @param {Array} options.canvasElements - Array of canvas elements
 * @param {Function} options.updateSpritesForTime - Function to update sprites for a specific time
 * @param {number} options.duration - Total duration of the project in seconds
 * @param {number} options.currentTime - Current time position (will be restored after capture)
 * @param {Object} options.canvasSize - The actual canvas size (e.g., {width: 1920, height: 1080})
 * @param {number} [options.captureTime] - Specific time to capture (defaults to duration/2)
 * @param {number} [options.width=320] - Thumbnail width
 * @param {number} [options.height=180] - Thumbnail height
 * @param {string} [options.format='image/png'] - Output format ('image/png' or 'image/jpeg')
 * @param {number} [options.quality=0.9] - JPEG quality (0-1)
 * @returns {Promise<string>} Base64 encoded image data URL
 */
export const captureCanvasThumbnail = async (options) => {
  const {
    viewport,
    canvasSize,
    pixiApp,
    selectElement,
    updateSpritesForTime,
    duration,
    currentTime,
    captureTime = duration / 2, // Default to middle of duration
    width = 320,
    height = 180,
    format = 'image/png',
    quality = 0.9
  } = options;

  if (!viewport || !pixiApp) {
    throw new Error('PIXI viewport and app are required');
  }

  // Track if ticker was running so we can restore state
  const wasTickerStarted = pixiApp.ticker.started;

  try {
    // Stop the ticker IMMEDIATELY to prevent rendering the stage 
    // while we move sprites around for the thumbnail
    if (wasTickerStarted) {
      pixiApp.ticker.stop();
    }

    // Hide transform controls (selection box) if present so they don't appear in thumbnail
    // usage of selectElement(null) caused UI flicker, so we hide the controls visually instead
    const transformControls = viewport.getChildByName('transform-controls');
    let controlsOriginallyVisible = false;
    if (transformControls) {
      controlsOriginallyVisible = transformControls.visible;
      transformControls.visible = false;
    }

    console.log(`[Thumbnail] Capturing at time: ${captureTime}s (freeze-capture-restore)`);

    // Step 1: Freeze all video texture auto-updates so the canvas doesn't render seek frames
    // const frozenVideos = freezeVideoTextures(pixiApp);

    // Step 2: Seek all sprites to capture time
    updateSpritesForTime(captureTime);

    // Step 3: Wait for all video seeks to complete
    // await waitForAllVideoSeeks(pixiApp, 2000);

    // Step 4: Manually update all video textures once for the capture
    // unfreezeAndUpdateVideoTextures(frozenVideos);

    // Step 5: Capture the thumbnail (renders to offscreen framebuffer, not screen)
    const exportMask = new PIXI.Graphics()
      .rect(0, 0, canvasSize.width, canvasSize.height)
      .fill(0xffffff);
    // pixiApp.stage.addChild(exportMask); // Causing white glitch, not needed for masking

    viewport.mask = exportMask;
    const dataUrl = await pixiApp.renderer.extract.base64({
      target: viewport,
      quality: 0.1
    });
    viewport.mask = null;
    exportMask.destroy();

    // Step 6: Freeze textures again before seeking back to original time
    // const frozenForRestore = freezeVideoTextures(pixiApp);

    // Step 7: Restore sprites to original time
    updateSpritesForTime(currentTime);

    // Step 8: Wait for restore seeks to complete
    await waitForAllVideoSeeks(pixiApp, 2000);

    // Step 9: Unfreeze and update
    // unfreezeAndUpdateVideoTextures(frozenForRestore);

    // Restore controls visibility
    if (transformControls) {
      transformControls.visible = controlsOriginallyVisible;
    }

    return dataUrl;
  } catch (error) {
    console.error('Error capturing canvas thumbnail:', error);
    throw error;
  } finally {
    // ALWAYS restart the ticker if it was running
    if (wasTickerStarted) {
      pixiApp.ticker.start();
    }
  }
};

/**
 * Freezes autoUpdate on  all video texture sources in the PIXI app.
 * Returns an array of sources that were frozen so they can be restored.
 */
function freezeVideoTextures(pixiApp) {
  const frozenSources = [];

  // Walk through all sprites in the stage to find video textures
  const walkChildren = (container) => {
    if (!container || !container.children) return;
    for (const child of container.children) {
      if (child.texture?.source && 'autoUpdate' in child.texture.source) {
        if (child.texture.source.autoUpdate) {
          child.texture.source.autoUpdate = false;
          frozenSources.push(child.texture.source);
        }
      }
      if (child.children) {
        walkChildren(child);
      }
    }
  };

  walkChildren(pixiApp.stage);
  return frozenSources;
}

/**
 * Re-enables autoUpdate and forces one texture update on all previously frozen sources.
 */
function unfreezeAndUpdateVideoTextures(frozenSources) {
  for (const source of frozenSources) {
    source.update();
    source.autoUpdate = true;
  }
}

/**
 * Waits for all video elements in the PIXI app to complete their pending seek.
 * @param {Object} pixiApp - PIXI Application
 * @param {number} timeoutMs - Maximum time to wait
 */
function waitForAllVideoSeeks(pixiApp, timeoutMs = 2000) {
  return new Promise((resolve) => {
    const videoElements = [];

    // Collect all video elements from sprites
    const walkChildren = (container) => {
      if (!container || !container.children) return;
      for (const child of container.children) {
        if (child.texture?.source?.resource instanceof HTMLVideoElement) {
          videoElements.push(child.texture.source.resource);
        }
        if (child.children) {
          walkChildren(child);
        }
      }
    };

    walkChildren(pixiApp.stage);

    // Filter to only videos that are currently seeking
    const seekingVideos = videoElements.filter(v => v.seeking);

    if (seekingVideos.length === 0) {
      // No videos seeking, resolve after a micro-tick for texture updates
      requestAnimationFrame(resolve);
      return;
    }

    let resolved = false;
    let remaining = seekingVideos.length;

    const onSeeked = () => {
      remaining--;
      if (remaining <= 0 && !resolved) {
        resolved = true;
        resolve();
      }
    };

    // Listen for seeked events on all seeking videos
    seekingVideos.forEach(video => {
      video.addEventListener('seeked', onSeeked, { once: true });
    });

    // Safety timeout
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        // Clean up listeners
        seekingVideos.forEach(video => {
          video.removeEventListener('seeked', onSeeked);
        });
        resolve();
      }
    }, timeoutMs);
  });
}

/**
 * Captures a thumbnail as a Blob for file operations
 * Converts base64 to Blob
 * @param {Object} options - Same options as captureCanvasThumbnail
 * @returns {Promise<Blob>} Image blob
 */
export const captureCanvasThumbnailAsBlob = async (options) => {
  // Simply get the base64 and convert to blob
  const dataUrl = await captureCanvasThumbnail(options);

  // Convert base64 to blob
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  return blob;
};

/**
 * Downloads the canvas thumbnail
 * @param {Object} options - Same options as captureCanvasThumbnail plus:
 * @param {string} [options.filename='canvas-thumbnail.png'] - Download filename
 */


/**
 * Generates multiple thumbnails at different time points
 * @param {Object} options - Base options for captureCanvasThumbnail
 * @param {Array<number>} [options.timePoints] - Array of time points to capture
 * @param {number} [options.count=5] - Number of evenly spaced thumbnails if timePoints not provided
 * @returns {Promise<Array<{time: number, dataUrl: string}>>} Array of thumbnails with their time points
 */
export const captureMultipleThumbnails = async (options) => {
  const {
    duration,
    timePoints,
    count = 5,
    ...captureOptions
  } = options;

  let captureTimes;

  if (timePoints) {
    captureTimes = timePoints;
  } else {
    // Generate evenly spaced time points
    captureTimes = [];
    const interval = duration / (count + 1);
    for (let i = 1; i <= count; i++) {
      captureTimes.push(interval * i);
    }
  }

  const thumbnails = [];

  for (const time of captureTimes) {
    try {
      const dataUrl = await captureCanvasThumbnail({
        ...captureOptions,
        duration,
        captureTime: time
      });

      thumbnails.push({
        time,
        dataUrl
      });
    } catch (error) {
      console.error(`Error capturing thumbnail at ${time}s:`, error);
    }
  }

  return thumbnails;
};

/**
 * Helper function to get thumbnail at specific percentage of duration
 * @param {Object} options - Same as captureCanvasThumbnail
 * @param {number} percentage - Percentage of duration (0-100)
 * @returns {Promise<string>} Base64 encoded image data URL
 */
export const captureCanvasThumbnailAtPercentage = async (options, percentage = 50) => {
  const { duration, ...rest } = options;
  const captureTime = (duration * percentage) / 100;

  return captureCanvasThumbnail({
    ...rest,
    duration,
    captureTime
  });
};